import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPane from '../../AdminPane';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismDialog,
  PrismLoading,
  PrismSelect,
} from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { useParkedImportSocket } from "../../../../hooks/useParkedImportSocket";
import {
  addProjectToFairEvent,
  removeProjectFromFairEvent,
  getProjectFairAdminData,
  importProjectFairProjects,
  importProjectFairLogos,
  assignProjectFairLogo,
  getParkedProjectFairImports,
  approveParkedProjectFairImport,
  discardParkedProjectFairImport,
  updateProjectFairEvent,
  updateProjectFairEventChapters,
  upsertProjectFairEvent,
  deleteProjectFairEvent,
} from "../../../../services/projectFairDetails";
import type { ParkedImportBatch } from "../../../../services/projectFairDetails";
import {
  buildImportMapping,
  buildImportedProject,
  collapseResubmissions,
  detectPeopleColumns,
  detectSubmissionMeta,
  findSubmitterHeader,
  readSpreadsheet,
  resolveTagNames,
  type PeopleColumns,
  type ResubmissionGroup,
  type SubmissionMeta,
} from "./spreadsheetImport";
import ProjectEditorModal from "../../../Projects/ProjectEditorModal";
import ProjectFair, { type FairDetails } from "../../../Projects/ProjectFair";
import type { Project as EditableProjectRecord } from "../../../Projects/projects.utils";
import PermissionLevel from "../../../../data/PermissionLevel";
import FairCopyModal from "./FairCopyModal";
import AttendeeToolsModal from "./AttendeeToolsModal";
import { ClosingBannerModal } from "./ClosingBannerModal";
import ApprovedProjectsModal from "./ApprovedProjectsModal";
import BulkImportModal from "./BulkImportModal";
import LogoMatchStepper from "./LogoMatchStepper";
import type { UnresolvedLogo } from "./LogoMatchStepper";
import SchedulePublishingModal from "./SchedulePublishingModal";
import ManageEventsModal from "./ManageEventsModal";
import NewEventModal from "./NewEventModal";
import "./ProjectFairs.css";

// Sentinel for the Event select's trailing "add" entry. A non-numeric value so it can
// never collide with an event id.
const NEW_EVENT_OPTION = "__new_event__";

export const TIMEZONE_OPTIONS = [
  { value: "America/New_York", display: "US — Eastern (New York)" },
  { value: "America/Chicago", display: "US — Central (Chicago)" },
  { value: "America/Denver", display: "US — Mountain (Denver)" },
  { value: "America/Los_Angeles", display: "US — Pacific (Los Angeles)" },
  { value: "Europe/London", display: "UK — London" },
  { value: "UTC", display: "UTC" },
];

export type LinkWindow = {
  fromUtc?: string;
  toUtc?: string;
  timezone?: string;
};

export type FairEvent = {
  id: number;
  year: number;
  chapter_id: number;
  chapter_key: string;
  title: string;
  friendly_date?: string;
  friendly_time?: string;
  friendly_time_2?: string;
  welcome_msg?: string;
  closing_msg?: string;
  directions?: string;
  support_channel_name?: string;
  support_channel_link?: string;
  survey_text?: string;
  survey_link?: string;
  // Server-computed from link_visibility.pageActive (see projectFairController's
  // isWindowLive) — read-only. Whether the fair page is "live" is derived from the
  // scheduled date range, not a manual flag; don't send this back on save.
  active?: boolean;
  print_enabled?: boolean;
  join_live_enabled?: boolean;
  video_stream_name?: string;
  video_stream_link?: string;
  spark_hub_name?: string;
  spark_hub_link?: string;
  link_visibility?: {
    joinChannel?: LinkWindow;
    videoStream?: LinkWindow;
    sparkHub?: LinkWindow;
    pageActive?: LinkWindow;
  };
  ApprovedProjects?: Array<EditableProjectRecord & { active?: boolean }>;
  Chapter?: { id: number; location: string };
  // Staged but not yet public. Set on creation and cleared by an explicit Publish; the
  // public year page filters these out entirely. See migration 0109.
  draft?: boolean;
  // Co-ownership: which chapters may fully edit this event. Falls back to a single-entry
  // array around `Chapter`/`chapter_id` for legacy events that predate co-ownership — see
  // serializeEvent on the server.
  Chapters?: Array<{ id: number; location: string }>;
  chapter_ids?: number[];
};

export type Chapter = { id: number; location: string };
export type FairProject = { id: number; title: string; year: number; chapter_id: number; project_fair_id?: number; tags?: Array<{ tag: { name: string } }> };

const nowYear = new Date().getFullYear();

const defaultLinkWindow = (timezone = "UTC"): LinkWindow => ({ fromUtc: "", toUtc: "", timezone });


export const toEventDraft = (event: FairEvent | null, timezone = "UTC") => {
  if (!event) {
    return {
      title: "",
      friendly_date: "",
      friendly_time: "",
      friendly_time_2: "",
      welcome_msg: "",
      closing_msg: "",
      directions: "",
      support_channel_name: "",
      support_channel_link: "",
      survey_text: "",
      survey_link: "",
      print_enabled: true,
      join_live_enabled: true,
      video_stream_name: "",
      video_stream_link: "",
      spark_hub_name: "",
      spark_hub_link: "",
      link_visibility: {
        joinChannel: defaultLinkWindow(timezone),
        videoStream: defaultLinkWindow(timezone),
        sparkHub: defaultLinkWindow(timezone),
        pageActive: defaultLinkWindow(timezone),
      },
    };
  }
  return {
    title: event.title || "",
    friendly_date: event.friendly_date || "",
    friendly_time: event.friendly_time || "",
    friendly_time_2: event.friendly_time_2 || "",
    welcome_msg: event.welcome_msg || "",
    closing_msg: event.closing_msg || "",
    directions: event.directions || "",
    support_channel_name: event.support_channel_name || "",
    support_channel_link: event.support_channel_link || "",
    survey_text: event.survey_text || "",
    survey_link: event.survey_link || "",
    print_enabled: event.print_enabled !== false,
    join_live_enabled: event.join_live_enabled !== false,
    video_stream_name: event.video_stream_name || "",
    video_stream_link: event.video_stream_link || "",
    spark_hub_name: event.spark_hub_name || "",
    spark_hub_link: event.spark_hub_link || "",
    link_visibility: {
      joinChannel: { ...defaultLinkWindow(timezone), ...(event.link_visibility?.joinChannel || {}) },
      videoStream: { ...defaultLinkWindow(timezone), ...(event.link_visibility?.videoStream || {}) },
      sparkHub: { ...defaultLinkWindow(timezone), ...(event.link_visibility?.sparkHub || {}) },
      pageActive: { ...defaultLinkWindow(timezone), ...(event.link_visibility?.pageActive || {}) },
    },
  };
};

export type EventDraft = ReturnType<typeof toEventDraft>;

export type CurrentUser = {
  chapter_id?: number;
  Role?: { permission_level?: number };
};

interface ProjectFairsProps {
  currentUser?: CurrentUser;
}

const ProjectFairs = ({ currentUser }: ProjectFairsProps) => {
  const isAdmin = (currentUser?.Role?.permission_level ?? 0) >= PermissionLevel.Admin;
  const userChapterId = currentUser?.chapter_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState<FairEvent[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [availableProjects, setAvailableProjects] = useState<FairProject[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(nowYear);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedProjectToAdd, setSelectedProjectToAdd] = useState<string>("");
  const [draft, setDraft] = useState<EventDraft>(toEventDraft(null));
  const [activeEventId, setActiveEventId] = useState<number | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({
    title: "",
    description: "",
    tags: "",
    chapter: "",
    logo: "",
    thanks: "",
  });
  const [peopleColumns, setPeopleColumns] = useState<PeopleColumns>({
    members: [],
    sponsorName: "",
    sponsorTitle: "",
  });
  const [submitterHeader, setSubmitterHeader] = useState("");
  const [submissionMeta, setSubmissionMeta] = useState<SubmissionMeta>({
    email: "",
    submittedAt: "",
    startedAt: "",
    id: "",
  });
  // Resubmission groups the officer has chosen to keep in full rather than collapse.
  const [keepAllFor, setKeepAllFor] = useState<string[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [crossChapterChoice, setCrossChapterChoice] = useState<"ignore" | "park" | "import">("park");
  const [availableTagNames, setAvailableTagNames] = useState<string[]>([]);
  const [parkedImports, setParkedImports] = useState<ParkedImportBatch[]>([]);
  const [parkedBusyId, setParkedBusyId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [includeLogos, setIncludeLogos] = useState(false);
  const [logoArchive, setLogoArchive] = useState<File | null>(null);
  const [importingLogos, setImportingLogos] = useState(false);
  const [unresolvedLogos, setUnresolvedLogos] = useState<UnresolvedLogo[]>([]);
  const [showLogoStepper, setShowLogoStepper] = useState(false);
  const [logoAssignedCount, setLogoAssignedCount] = useState(0);
  const [logoEventId, setLogoEventId] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editingProject, setEditingProject] = useState<EditableProjectRecord | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([nowYear]);
  const [openSection, setOpenSection] = useState<"copy" | "schedule" | "access" | "closing" | "projects" | "import" | "identity" | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importFileKey, setImportFileKey] = useState(0);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  // Bumped whenever the New Event dialog closes to force the Events <PrismSelect> to remount.
  // The web component keeps its own internal selection state once the user picks "+ Add a
  // new event…"; simply leaving the `value` prop unchanged (the previously-selected real
  // event never changed) does not snap the control back, so the DOM is left showing the
  // transient option after Cancel — and re-picking that same still-selected option fires no
  // change event at all. Remounting is the reliable way to force the underlying element back
  // in sync with React's model of the actual selection.
  const [eventSelectResetKey, setEventSelectResetKey] = useState(0);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventError, setCreateEventError] = useState("");
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  // Surfaced inside ManageEventsModal. Toasts cannot be used for failures raised while a
  // prism-dialog is open — see ManageEventsModalProps.actionError.
  const [eventActionError, setEventActionError] = useState("");

  const importHeaders = useMemo(() => {
    if (!importRows.length) return [];
    return Object.keys(importRows[0]);
  }, [importRows]);

  // Fetch the entire dataset (every year/chapter) once, unfiltered, instead of re-fetching
  // per year/chapter selection — every toggle then just re-derives its view from local state
  // (see `activeEvent`/`candidateProjects` below), so switching years or chapters feels
  // instant instead of round-tripping to the server on every click.
  // `silent` refreshes the data in place without raising the global loading flag. The flag
  // swaps the whole pane for a spinner, which is right on first mount but jarring after a
  // save — the user loses their scroll position and watches the page rebuild itself. Every
  // post-mutation refresh is silent so a save settles back into the page the way Cancel does.
  const loadAll = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await getProjectFairAdminData();
      const loadedEvents: FairEvent[] = data.events || [];
      const loadedChapters: Chapter[] = data.chapters || [];
      setEvents(loadedEvents);
      setChapters(loadedChapters);
      setAvailableProjects(data.availableProjects || []);
      setAvailableTagNames(
        (data.tags || []).map((tag: { name?: string }) => String(tag?.name || "")).filter(Boolean)
      );
      const years = new Set<number>([nowYear, ...loadedEvents.map((event) => event.year)]);
      setAvailableYears([...years].sort((a, b) => b - a));
      setSelectedChapterId((current) => {
        if (current && loadedChapters.some((chapter) => chapter.id === current)) return current;
        // Mirrors Officer Management: land on the signed-in user's own chapter when they
        // have one, since that's the data they're here to manage. Admins with no chapter
        // (or a chapter that no longer exists) fall back to the first tab.
        const ownChapter = loadedChapters.find((chapter) => chapter.id === userChapterId);
        return ownChapter?.id ?? loadedChapters[0]?.id ?? null;
      });
    } catch (error) {
      toast.error("Failed to load Project Fairs admin data.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadParkedImports = async () => {
    try {
      const batches = await getParkedProjectFairImports();
      setParkedImports(Array.isArray(batches) ? batches : []);
    } catch (error) {
      // A failed queue fetch must not block the rest of the pane; the section just stays empty.
      console.error(error);
    }
  };

  useEffect(() => {
    loadParkedImports();
  }, []);

  // Live-refresh the queue. Previously the list above was the only fetch, so a batch
  // another officer submitted while this pane was open stayed invisible until a manual
  // browser reload. Only the parked list is re-read — deliberately not loadAll(), because
  // refreshing `events` changes `activeEvent`'s identity, and ManageEventsModal resets its
  // title/anchor/owner fields off that, which would wipe whatever an admin was mid-way
  // through typing in an open modal.
  const handleParkedImportSignal = useCallback(() => {
    loadParkedImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useParkedImportSocket({ onChange: handleParkedImportSignal });

  const handleApproveParked = async (batch: ParkedImportBatch) => {
    setParkedBusyId(batch.id);
    try {
      const { created, duplicates, unmatchedTags } = (await approveParkedProjectFairImport(batch.id)) || {};
      await Promise.all([loadAll({ silent: true }), loadParkedImports()]);
      toast.success(
        duplicates > 0
          ? `Imported ${created} project${created === 1 ? "" : "s"}. Skipped ${duplicates} that already existed.`
          : `Imported ${created} project${created === 1 ? "" : "s"} from ${batch.submitter?.name || "another chapter"}.`
      );
      if (Array.isArray(unmatchedTags) && unmatchedTags.length) {
        toast.error(`These tags don't exist yet and were skipped: ${unmatchedTags.join(", ")}.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to approve the import.");
    } finally {
      setParkedBusyId(null);
    }
  };

  const handleDiscardParked = async (batch: ParkedImportBatch) => {
    setParkedBusyId(batch.id);
    try {
      await discardParkedProjectFairImport(batch.id);
      await loadParkedImports();
      toast.success("Import declined.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to decline the import.");
    } finally {
      setParkedBusyId(null);
    }
  };

  useEffect(() => {
    resetImportState();
  }, [selectedYear, selectedChapterId]);

  // A tab switch is a hard context change — nothing from the previous chapter (year pin,
  // selected event, hot seat) is meaningful for the new one. Skips the very first time
  // `selectedChapterId` goes from null to a value (initial load landing on the user's
  // chapter), since that's not a user-driven switch and the year is already at its default.
  const prevChapterIdRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevChapterIdRef.current;
    prevChapterIdRef.current = selectedChapterId;
    if (prev !== null && selectedChapterId !== null && prev !== selectedChapterId) {
      setSelectedYear(nowYear);
      setSelectedEventId(null);
      // Any open tile modal (Manage Events in particular) is rendered against the outgoing
      // chapter's activeEvent. Leaving it open lets `activeEvent` go null underneath a still
      // -mounted PrismDialog: its slotted footer buttons unmount conditionally, but
      // prism-dialog has already re-parented that DOM node into its own shadow root, so
      // React's teardown throws (`removeChild` on a node it no longer owns). Closing here
      // unmounts the dialog cleanly before the chapter data changes.
      setOpenSection(null);
    }
  }, [selectedChapterId]);

  const ownerIdsOf = (event: FairEvent | null | undefined): number[] => {
    if (!event) return [];
    return event.chapter_ids?.length ? event.chapter_ids : [event.chapter_id];
  };

  // A chapter tab now shows every event that chapter co-owns for the selected year, not
  // just the one it created — a chapter can own its own default fair plus a joint virtual
  // fair or a hand-curated expo at the same time.
  const eventsForChapter = useMemo(() => {
    if (!selectedChapterId) return [];
    return events.filter((event) => event.year === selectedYear && ownerIdsOf(event).includes(selectedChapterId));
  }, [events, selectedYear, selectedChapterId]);

  // Anchors are unique per year across every chapter, so the conflict set the New Event
  // modal checks against is year-scoped, not chapter-scoped.
  const anchorsForYear = useMemo(
    () => events.filter((event) => event.year === selectedYear).map((event) => event.chapter_key).filter(Boolean),
    [events, selectedYear]
  );

  // The Event select always renders, so it has to say something useful even with nothing
  // to select. The placeholder carries the nudge; the trailing entry is a shortcut into
  // the same dialog as "+ New Event" in the Manage Events pop-up. Sorted alphabetically by
  // title so the dropdown doesn't just reflect creation order.
  const eventSelectOptions = useMemo(() => {
    const options = [...eventsForChapter]
      .sort((a, b) => (a.chapter_key || "").localeCompare(b.chapter_key || "", undefined, { sensitivity: "base" }))
      .map((event) => ({
        value: String(event.id),
        display: `#${event.chapter_key}`,
      }));
    if (!options.length) {
      options.push({
        value: "",
        display: selectedChapterId
          ? `No events yet for ${selectedYear} — add one to get started`
          : "Select a chapter to see its events",
      });
    }
    if (selectedChapterId) {
      options.push({ value: NEW_EVENT_OPTION, display: "+ Add a new event…" });
    }
    return options;
  }, [eventsForChapter, selectedChapterId, selectedYear]);

  useEffect(() => {
    // Fall back to the chapter's first event whenever the chapter/year changes, or the
    // previously-picked event no longer belongs to this chapter (e.g. co-owners changed).
    if (selectedEventId && eventsForChapter.some((event) => event.id === selectedEventId)) return;
    setSelectedEventId(eventsForChapter[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsForChapter]);

  const activeEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return events.find((event) => event.id === selectedEventId) || null;
  }, [events, selectedEventId]);

  useEffect(() => {
    setActiveEventId(activeEvent?.id || null);
    setDraft(toEventDraft(activeEvent));
  }, [activeEvent]);

  const candidateProjects = useMemo(() => {
    if (!activeEvent) return [];
    const assignedIds = new Set((activeEvent.ApprovedProjects || []).map((project) => project.id));
    return availableProjects.filter((project) => project.year === selectedYear && !assignedIds.has(project.id));
  }, [activeEvent, availableProjects, selectedYear]);

  const selectedChapter = useMemo(
    () => chapters.find((chapter) => chapter.id === selectedChapterId) || null,
    [chapters, selectedChapterId]
  );

  // Officers can view every chapter's fair data but may only edit events their chapter
  // co-owns; admins can edit anything. Mirrors the server-side guard in
  // projectFairController's blockIfCannotEditEvent, so the UI doesn't offer edits the API
  // would reject anyway. Falls back to a plain chapter-tab check when no event exists yet
  // (e.g. a brand-new chapter/year combo with nothing to create-and-own).
  const canEditSelectedChapter =
    isAdmin ||
    (activeEvent
      ? !!userChapterId && ownerIdsOf(activeEvent).includes(userChapterId)
      : !!selectedChapterId && selectedChapterId === userChapterId);

  // Feeds the "Preview" modal with the unsaved draft rendered on the real public
  // /project-fair page component — nothing here is persisted, see ProjectFair's
  // `previewOverride` prop.
  const previewFairDetails: FairDetails = useMemo(
    () => ({
      id: activeEvent?.id,
      chapter_id: selectedChapterId ?? undefined,
      chapter_key: activeEvent?.chapter_key || "",
      Chapter: selectedChapter ? { id: selectedChapter.id, location: selectedChapter.location } : activeEvent?.Chapter,
      title: draft.title,
      friendly_date: draft.friendly_date,
      friendly_time: draft.friendly_time,
      friendly_time_2: draft.friendly_time_2,
      welcome_msg: draft.welcome_msg,
      closing_msg: draft.closing_msg,
      directions: draft.directions,
      survey_link: draft.survey_link,
      survey_text: draft.survey_text,
      support_channel_link: draft.support_channel_link,
      support_channel_name: draft.support_channel_name,
      print_enabled: draft.print_enabled,
      join_live_enabled: draft.join_live_enabled,
      video_stream_name: draft.video_stream_name,
      video_stream_link: draft.video_stream_link,
      spark_hub_name: draft.spark_hub_name,
      spark_hub_link: draft.spark_hub_link,
      link_visibility: draft.link_visibility,
      ApprovedProjects: activeEvent?.ApprovedProjects || [],
    }),
    [activeEvent, selectedChapter, selectedChapterId, draft]
  );

  // Every section past Event Identity edits one specific event, so one must already be
  // selected. Nothing here creates an event implicitly any more: an event used to be
  // conjured by the first save of *any* card, and since a fresh event has no pageActive
  // window (which reads as "live") it landed on the public year page immediately — an
  // untitled, dateless, projectless shell on the most externally-visible page we have.
  // Minting an event is now a deliberate act through "+ New Event".
  const requireEvent = (): number | null => {
    if (activeEventId) return activeEventId;
    toast.error("Create an event for this chapter and year first.");
    return null;
  };

  // An event exists and is selected — the single real prerequisite for editing anything.
  // The section cards gate on this rather than on a fixed order between them, because copy,
  // schedule, tools, links, projects and import genuinely have no dependencies on each
  // other; only on there being an event to attach them to.
  const hasActiveEvent = !!activeEvent;
  const sectionsUnlocked = !!selectedChapterId && hasActiveEvent && canEditSelectedChapter;

  const setEventDraftState = async (nextDraft: boolean) => {
    const eventId = requireEvent();
    if (!eventId) return;
    setPublishing(true);
    try {
      await updateProjectFairEvent(eventId, { draft: nextDraft });
      await loadAll({ silent: true });
      toast.success(
        nextDraft
          ? "Event returned to draft. It is no longer listed publicly."
          : "Event published. It now appears on the public project fair page."
      );
    } catch (error) {
      // The publish checkbox lives inside the Manage Events dialog, so a toast here would
      // be painted behind it.
      setEventActionError(error instanceof Error ? error.message : "Failed to change the event's visibility.");
    } finally {
      setPublishing(false);
    }
  };

  const save = async () => {
    const eventId = requireEvent();
    if (!eventId) return;
    setSaving(true);
    try {
      await updateProjectFairEvent(eventId, draft);
      await loadAll({ silent: true });
      toast.success("Project fair settings saved.");
    } catch {
      toast.error("Failed to save project fair settings.");
    } finally {
      setSaving(false);
    }
  };

  // Each section modal shares the same underlying save() (the backend only has one event
  // record to update, not a separate draft-per-section) — Save persists the whole draft and
  // closes the modal; Cancel discards any unsaved edits by reverting to the last-saved values.
  const saveSection = async () => {
    await save();
    setOpenSection(null);
  };

  const cancelSection = () => {
    setDraft(toEventDraft(activeEvent));
    setOpenSection(null);
  };

  const closeNewEventModal = () => {
    setCreateEventError("");
    setShowNewEventModal(false);
    setEventSelectResetKey((key) => key + 1);
  };

  const createEvent = async (payload: { title: string; chapter_key: string; chapter_ids: number[] }) => {
    if (!payload.chapter_ids.length) {
      setCreateEventError("Pick at least one owning chapter.");
      return;
    }
    setCreatingEvent(true);
    setCreateEventError("");
    try {
      const created = await upsertProjectFairEvent(selectedYear, payload.chapter_ids[0], {
        title: payload.title,
        chapter_key: payload.chapter_key,
        chapter_ids: payload.chapter_ids,
      });
      await loadAll({ silent: true });
      setSelectedEventId(created.id);
      closeNewEventModal();
      toast.success(`Created "${payload.title}".`);
    } catch (error) {
      // Reported inside the dialog rather than only as a toast: the dialog stays open on
      // failure, and a toast rendered behind it reads as the button doing nothing at all.
      setCreateEventError(error instanceof Error ? error.message : "Failed to create the new event.");
    } finally {
      setCreatingEvent(false);
    }
  };

  const saveIdentity = async (payload: { title: string; chapter_key: string; chapter_ids: number[] }) => {
    const eventId = activeEvent?.id;
    if (!eventId) {
      setEventActionError("Select an event before saving.");
      return;
    }
    setEventActionError("");
    setSavingIdentity(true);
    try {
      await updateProjectFairEvent(eventId, { chapter_key: payload.chapter_key, title: payload.title });
      await updateProjectFairEventChapters(eventId, payload.chapter_ids);
      await loadAll({ silent: true });
      setOpenSection(null);
      toast.success("Event saved.");
    } catch (error) {
      setEventActionError(error instanceof Error ? error.message : "Failed to save event.");
    } finally {
      setSavingIdentity(false);
    }
  };

  const deleteEvent = async () => {
    // Read the id straight off `activeEvent` rather than going through `requireEvent()`.
    // `requireEvent()` reads `activeEventId`, a state value mirrored from `activeEvent` by
    // an effect; while the modal renders its delete UI off `activeEvent` itself. If the two
    // ever desync the delete silently no-ops, and the "create an event first" toast it
    // raises instead is invisible behind the top-layer dialog.
    const eventId = activeEvent?.id;
    if (!eventId) {
      setEventActionError("Select an event before deleting.");
      return;
    }
    setEventActionError("");
    setDeletingEvent(true);
    try {
      await deleteProjectFairEvent(eventId);
      // Close the dialog *before* clearing the selection it renders from. The modal body
      // falls back to "Select or create an event first." whenever `activeEvent` is absent,
      // so clearing the selection while it is still open flashes that empty state at the
      // user as though they had done something wrong — and the awaited reload below holds
      // it on screen long enough to read. Closing first means the empty state never paints.
      setOpenSection(null);
      // Force the chapter/year effect to pick a new default event rather than pointing at
      // the one that was just deleted.
      setSelectedEventId(null);
      await loadAll({ silent: true });
      // Toast last: a success toast raised while the dialog is still open would be painted
      // behind it.
      toast.success("Event deleted.");
    } catch (error) {
      setEventActionError(error instanceof Error ? error.message : "Failed to delete the event.");
    } finally {
      setDeletingEvent(false);
    }
  };

  const copyEventLink = async () => {
    if (!activeEvent) return;
    const url = `${window.location.origin}/project-fair/${selectedYear}#${activeEvent.chapter_key}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Event link copied to clipboard.");
    } catch {
      toast.error("Could not copy the link. Copy it manually: " + url);
    }
  };

  const addProject = async () => {
    const eventId = requireEvent();
    if (!eventId) return;
    if (!selectedProjectToAdd) {
      toast.error("Select a project to add.");
      return;
    }
    try {
      await addProjectToFairEvent(eventId, selectedProjectToAdd);
      setSelectedProjectToAdd("");
      await loadAll({ silent: true });
      toast.success("Project added to fair event.");
    } catch {
      toast.error("Failed to add project.");
    }
  };

  const removeProject = async (project: EditableProjectRecord) => {
    const eventId = activeEvent?.id;
    if (!eventId) return;
    try {
      await removeProjectFromFairEvent(eventId, project.id);
      await loadAll({ silent: true });
      toast.success(`${project.title} removed from this fair. The project itself was not deleted.`);
    } catch {
      toast.error("Failed to remove project from this fair.");
    }
  };

  const openCreateProject = async () => {
    const eventId = requireEvent();
    if (!eventId) return;
    setEditingProject(null);
    setEditorMode("create");
    setEditorOpen(true);
  };

  const openEditProject = (project: EditableProjectRecord) => {
    setEditingProject(project);
    setEditorMode("edit");
    setEditorOpen(true);
  };

  const handleProjectEditorSaved = async () => {
    setEditorOpen(false);
    setEditingProject(null);
    await loadAll();
  };

  const resetImportState = () => {
    setImportRows([]);
    setImportMapping({ title: "", description: "", tags: "", chapter: "", logo: "", thanks: "" });
    setPeopleColumns({ members: [], sponsorName: "", sponsorTitle: "" });
    setSubmitterHeader("");
    setSubmissionMeta({ email: "", submittedAt: "", startedAt: "", id: "" });
    setKeepAllFor([]);
    setImportFileName("");
    setCrossChapterChoice("park");
    setShowImportConfirm(false);
    setIncludeLogos(false);
    setLogoArchive(null);
    // Bump the mount key so PrismInputFile fully remounts and drops its displayed filename.
    setImportFileKey((key) => key + 1);
  };

  const handleImportFile = async (file: File) => {
    try {
      const { headers, rows } = await readSpreadsheet(file);
      if (!rows.length) {
        toast.error("No rows were found in the uploaded spreadsheet.");
        resetImportState();
        return;
      }
      setImportRows(rows);
      setImportMapping(buildImportMapping(headers));
      setPeopleColumns(detectPeopleColumns(headers));
      setSubmitterHeader(findSubmitterHeader(headers));
      setSubmissionMeta(detectSubmissionMeta(headers));
      setKeepAllFor([]);
      setImportFileName(file.name);
    } catch (error) {
      console.error(error);
      toast.error("Could not read the uploaded spreadsheet. Use CSV or XLSX data.");
      resetImportState();
    }
  };

  /**
   * Repeat submissions from the same person, collapsed to their latest response. Runs before
   * anything else reads the rows, so the preview, the chapter split and the import all agree
   * on one row per project.
   */
  const dedupedImport = useMemo(() => {
    if (!importMapping.title) return { rows: importRows, groups: [] as ResubmissionGroup[] };
    return collapseResubmissions(importRows, importMapping, submissionMeta, submitterHeader);
  }, [importRows, importMapping, submissionMeta, submitterHeader]);

  /**
   * The rows actually imported. A group the officer chose to keep in full has its superseded
   * rows put back — one person can legitimately own two projects, and the sheet can't tell
   * that apart from a title correction.
   */
  const effectiveImportRows = useMemo(() => {
    if (!keepAllFor.length) return dedupedImport.rows;
    const restored = new Set<Record<string, string>>();
    dedupedImport.groups.forEach((group) => {
      if (keepAllFor.includes(group.submitter)) group.superseded.forEach((row) => restored.add(row));
    });
    if (!restored.size) return dedupedImport.rows;
    const order = new Map(importRows.map((row, index) => [row, index]));
    return [...dedupedImport.rows, ...restored].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  }, [dedupedImport, keepAllFor, importRows]);

  const importPreviewRows = useMemo(() => effectiveImportRows.slice(0, 3), [effectiveImportRows]);

  /**
   * Turns the uploaded sheet into import payloads, split by which chapter each row belongs
   * to. Rows naming another chapter are held separately rather than merged in: they are
   * never imported here, only ignored or parked for that chapter to approve.
   */
  const partitionedImportRows = useMemo(() => {
    const mine: Array<Record<string, unknown>> = [];
    const others: Array<{ chapter_id: number; row: Record<string, unknown> }> = [];
    if (!importMapping.title) return { mine, others };

    const chapterIdFor = (name: string) => {
      const normalized = name.trim().toLowerCase();
      if (!normalized) return null;
      const match = chapters.find((chapter) => chapter.location.trim().toLowerCase() === normalized);
      return match ? match.id : null;
    };

    effectiveImportRows.forEach((row) => {
      const project = buildImportedProject(row, importMapping, peopleColumns, submitterHeader);
      if (!project.title) return;
      const targetId = importMapping.chapter ? chapterIdFor(project.chapter) : null;
      // A chapter value we can't resolve to a real chapter is treated as the officer's own,
      // so a typo or an unfamiliar label never silently drops the row.
      if (targetId && targetId !== selectedChapterId) {
        others.push({ chapter_id: targetId, row: project });
      } else {
        mine.push(project);
      }
    });

    return { mine, others };
  }, [effectiveImportRows, importMapping, peopleColumns, submitterHeader, chapters, selectedChapterId]);

  /** Chapters named by the sheet that aren't the one being imported into, for the prompt. */
  const crossChapterSummary = useMemo(() => {
    const counts = new Map<number, number>();
    partitionedImportRows.others.forEach((entry) => {
      counts.set(entry.chapter_id, (counts.get(entry.chapter_id) || 0) + 1);
    });
    return [...counts.entries()].map(([chapterId, count]) => ({
      chapterId,
      count,
      location: chapters.find((chapter) => chapter.id === chapterId)?.location || "Another chapter",
    }));
  }, [partitionedImportRows, chapters]);

  /**
   * Which tags on the sheet can't be resolved against the existing taxonomy. Surfaced before
   * the import runs, because the server matches tags rather than creating them — an
   * unrecognised label would otherwise be quietly dropped.
   */
  const importTagPreview = useMemo(() => {
    const names = [
      ...partitionedImportRows.mine,
      ...partitionedImportRows.others.map((entry) => entry.row),
    ].flatMap((project) => ((project as { tags?: string[] }).tags || []));
    const existing = availableTagNames;
    if (!names.length || !existing.length) return { matched: [], unmatched: [] };
    return resolveTagNames(names, existing);
  }, [partitionedImportRows, availableTagNames]);

  const triggerImportConfirmation = () => {
    if (!selectedChapterId) {
      toast.error("Select a chapter before importing projects.");
      return;
    }
    if (!importRows.length) {
      toast.error("Upload a spreadsheet before import.");
      return;
    }
    if (!partitionedImportRows.mine.length && !partitionedImportRows.others.length) {
      toast.error("No valid project rows were found in the spreadsheet");
      return;
    }

    setShowImportConfirm(true);
  };

  const importProjects = async () => {
    if (!selectedChapterId) {
      toast.error("Select a chapter before importing projects.");
      return;
    }

    const { mine, others } = partitionedImportRows;
    if (!mine.length && !(crossChapterChoice !== "ignore" && others.length)) {
      toast.error("No valid project rows were found in the spreadsheet");
      return;
    }

    setImporting(true);
    setShowImportConfirm(false);
    try {
      // Import is only reachable once an event is active (sectionsUnlocked requires
      // hasActiveEvent), so activeEventId is always the event the officer has open here.
      // The server honors it as long as it's still a real event for this chapter/year —
      // without this, a chapter that owns two events in a year always had imports land on
      // whichever was created first, regardless of which one the officer was looking at.
      const {
        event_id: importedEventId,
        created,
        duplicates,
        unmatchedTags,
        parked,
        imported_elsewhere: importedElsewhere,
      } = await importProjectFairProjects({
        year: selectedYear,
        chapter_id: selectedChapterId,
        event_id: activeEventId ?? undefined,
        rows: mine,
        cross_chapter: crossChapterChoice,
        parked_rows: crossChapterChoice !== "ignore" ? others : [],
        source_file_name: importFileName,
      });
      await loadAll();
      if (duplicates > 0) {
        toast.success(
          `Imported ${created} new project${created === 1 ? "" : "s"}. Skipped ${duplicates} row${
            duplicates === 1 ? "" : "s"
          } that matched an existing project — edit those from Manage Projects.`
        );
      } else {
        toast.success(`Imported ${created} project${created === 1 ? "" : "s"} to the fair event.`);
      }

      if (Array.isArray(parked) && parked.length) {
        const summary = parked.map((batch: { rows: number; chapter: string }) => `${batch.rows} to ${batch.chapter}`).join(", ");
        toast.success(`Sent ${summary} for their officers to approve.`);
      }

      if (Array.isArray(importedElsewhere) && importedElsewhere.length) {
        const summary = importedElsewhere
          .map((entry: { created: number; chapter: string }) => `${entry.created} to ${entry.chapter}`)
          .join(", ");
        toast.success(`Imported ${summary} on their behalf — no officer review was required.`);
      }

      if (Array.isArray(unmatchedTags) && unmatchedTags.length) {
        toast.error(
          `These tags don't exist yet and were skipped: ${unmatchedTags.join(", ")}. Add them under Tags, then set them on the projects.`
        );
      }

      // Logos are a second pass on purpose: matching needs the projects to exist first, and
      // a logo failure must never invalidate a successful row import. The parked batch ids
      // go along so logos belonging to other chapters' rows can be staged with their batch
      // instead of being offered up as unmatched against this chapter's projects.
      if (includeLogos && logoArchive && importedEventId) {
        const parkedBatchIds = (Array.isArray(parked) ? parked : [])
          .map((batch: { id?: number }) => batch?.id)
          .filter((id): id is number => Number.isInteger(id));
        // Rows dropped outright under the "ignore" choice never get a project on this event.
        // Naming their filenames lets the server recognize and discard their logos instead of
        // scoring them against this chapter's project titles and surfacing false "unresolved"
        // matches for the operator to sort through.
        const ignoredRows = crossChapterChoice === "ignore" ? others.map((entry) => entry.row) : [];
        await importLogos(importedEventId, parkedBatchIds, mine, ignoredRows);

        // Rows an admin imported straight into another chapter's fair produced projects on
        // *that* chapter's event, so their logos have to be reconciled against it. Each pass
        // is given the whole declared-name list and keeps only the titles it owns.
        for (const entry of Array.isArray(importedElsewhere) ? importedElsewhere : []) {
          const otherEventId = (entry as { event_id?: number })?.event_id;
          if (!Number.isInteger(otherEventId)) continue;
          await importLogos(otherEventId as number, [], others.map((row) => row.row ?? row));
        }
      }
      resetImportState();
    } catch (error) {
      console.error(error);
      toast.error("Failed to import projects from the spreadsheet.");
    } finally {
      setImporting(false);
    }
  };

  const importLogos = async (
    eventId: number,
    parkedBatchIds: number[] = [],
    rowsForNames: Array<Record<string, unknown>> = partitionedImportRows.mine,
    ignoredRowsForNames: Array<Record<string, unknown>> = [],
  ) => {
    if (!logoArchive) return;
    setImportingLogos(true);
    try {
      // Filenames the spreadsheet named per project. Passing them lets the server pair each
      // archive entry by what the submitter actually uploaded instead of scoring its name
      // against project titles.
      const nameEntry = (row: Record<string, unknown>) => ({
        title: String((row as { title?: string }).title || ""),
        file_name: String((row as { logo_file_name?: string }).logo_file_name || ""),
      });
      const logoNames = rowsForNames.map(nameEntry).filter((entry) => entry.title && entry.file_name);
      const ignoredLogoNames = ignoredRowsForNames.map(nameEntry).filter((entry) => entry.title && entry.file_name);
      const result = await importProjectFairLogos(eventId, logoArchive, logoNames, parkedBatchIds, ignoredLogoNames);
      setLogoEventId(eventId);
      setLogoAssignedCount(result.assigned.length);
      if (result.rejected.length) {
        // Not an error: archives routinely carry a nested zip, a readme or a Thumbs.db.
        // Naming the entries lets the officer tell "expected clutter" from "my logo was
        // silently dropped" without re-opening the archive.
        const names = result.rejected
          .map((entry) => (entry.path || "").split("/").pop())
          .filter(Boolean);
        toast.info(
          `Skipped ${names.length} non-image entr${names.length === 1 ? "y" : "ies"}: ${names.join(", ")}.`
        );
      }
      const stagedCount = Array.isArray(result.staged) ? result.staged.length : 0;
      if (stagedCount) {
        // Says out loud that these logos were routed, not lost: they belong to rows parked
        // for another chapter and will be attached when that chapter approves its batch.
        toast.info(
          `Held ${stagedCount} logo${stagedCount === 1 ? "" : "s"} with the parked rows — they'll attach when the owning chapter approves.`
        );
      }
      const discardedCount = Array.isArray(result.discarded) ? result.discarded.length : 0;
      if (discardedCount) {
        // Confirms these were recognized and dropped on purpose — not lost, not a mystery
        // file needing a decision — because their rows were explicitly ignored.
        toast.info(
          `Discarded ${discardedCount} logo${discardedCount === 1 ? "" : "s"} belonging to ignored rows.`
        );
      }
      if (result.unresolved.length) {
        setUnresolvedLogos(result.unresolved);
        setShowLogoStepper(true);
        toast.success(`Matched ${result.assigned.length} logo(s). ${result.unresolved.length} need(s) your review.`);
      } else {
        toast.success(`Matched all ${result.assigned.length} logo(s).`);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to import logos.");
    } finally {
      setImportingLogos(false);
    }
  };

  const assignUnresolvedLogo = async (logo: UnresolvedLogo, projectId: number) => {
    if (!logoEventId || !logo.data) return;
    try {
      await assignProjectFairLogo(logoEventId, projectId, { data: logo.data, sourceName: logo.source_name });
      setLogoAssignedCount((count) => count + 1);
      await loadAll();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to assign that logo.");
    }
  };

  if (loading) {
    return (
      <div className="project-fairs-loading">
        <PrismLoading as="spinner" container="inline" />
      </div>
    );
  }

  // Each card opens its section with a subject-matter emoji rather than a row of identical
  // "Edit" buttons. The glyph is decorative (aria-hidden in the markup below); the button's
  // accessible name and tooltip still say "Edit <section>", so nothing is conveyed by the
  // emoji alone. Emoji are kept to long-established codepoints so they render on older OSes
  // instead of falling back to tofu.
  const sectionCards: Array<{
    key: "copy" | "schedule" | "access" | "closing" | "projects" | "import";
    title: string;
    summary: string;
    emoji: string;
  }> = [
    {
      key: "copy",
      emoji: "✍️",
      title: "Fair Copy & Messaging",
      summary: draft.title || "Untitled event",
    },
    {
      key: "schedule",
      emoji: "📅",
      title: "Schedule & Publishing",
      summary: `${draft.friendly_date || "No date set"}, ${draft.friendly_time || "no time set"} - Page is ${
        activeEvent?.active === false ? "hidden" : "live"
      }`,
    },
    {
      key: "import",
      emoji: "📥",
      title: "Bulk Import",
      summary: "Upload a CSV/XLSX spreadsheet to add projects in bulk",
    },
    {
      key: "projects",
      emoji: "🗂️",
      title: "Manage Projects",
      summary: `${activeEvent?.ApprovedProjects?.length || 0} project(s) assigned`,
    },
    {
      key: "access",
      emoji: "🧰",
      title: "Attendee Tools",
      summary: `Print ${draft.print_enabled ? "on" : "off"} · Join Live ${draft.join_live_enabled ? "on" : "off"}`,
    },
    {
      key: "closing",
      // U+1F44B, not the chequered flag that was here before: 🏁 rendered as tofu on at
      // least one Windows build. The waving hand is an older, more broadly shipped
      // codepoint and reads as the send-off this card actually configures.
      emoji: "👋",
      title: "Closing Banner",
      summary: `Channel ${draft.support_channel_name || draft.support_channel_link ? "on" : "off"} - Thank you ${
        draft.closing_msg ? "set" : "not set"
      } - Feedback ${draft.survey_text || draft.survey_link ? "on" : "off"}`,
    },
  ];

  return (
    <AdminPane as="div" width="full" className="project-fairs-pane">
      <PrismBanner tone="info">
        Configure fair copy, schedule, officer tools, and the public closing banner. Any field left blank will
        simply not be displayed on the public fair page. The public route remains{" "}
        <code>/project-fair/:year#chapter</code>.
      </PrismBanner>

      {/* Prime placement: this is a call to action, not a status readout, so it renders
          above everything else on the page rather than at the bottom where it's easy to
          miss. */}
      {parkedImports.length ? (
        <section className="project-fairs-cards" aria-live="polite">
          <PrismCard display="static" headline="Imports awaiting your review" headlineTag="h3">
            <p className="project-fairs-card-summary">
              Another chapter uploaded a spreadsheet that included projects for you. Nothing is added to your
              fair until you approve it.
            </p>
            <div className="project-fairs-project-list">
              {parkedImports.map((batch) => (
                <div key={batch.id} className="project-fairs-parked-item">
                  <div className="project-fairs-parked-meta">
                    <strong>
                      {batch.rows.length} project{batch.rows.length === 1 ? "" : "s"} for {batch.year}
                    </strong>
                    <small>
                      From {batch.submitter?.name || "an officer"}
                      {batch.source_file_name ? ` • ${batch.source_file_name}` : ""}
                    </small>
                    <small>
                      {batch.rows
                        .map((row) => String((row as { title?: string }).title || "Untitled"))
                        .join(", ")}
                    </small>
                    {batch.unmatched_tags?.length ? (
                      <small>Tags that will be skipped: {batch.unmatched_tags.join(", ")}</small>
                    ) : null}
                  </div>
                  <div className="project-fairs-parked-actions">
                    <PrismButton
                      tone="secondary"
                      display="fill"
                      disabled={parkedBusyId === batch.id || !canEditSelectedChapter}
                      onPrismClick={() => handleApproveParked(batch)}
                    >
                      Approve
                    </PrismButton>
                    <PrismButton
                      tone="secondary"
                      display="outline"
                      disabled={parkedBusyId === batch.id || !canEditSelectedChapter}
                      onPrismClick={() => handleDiscardParked(batch)}
                    >
                      Decline
                    </PrismButton>
                  </div>
                </div>
              ))}
            </div>
          </PrismCard>
        </section>
      ) : null}

      {!canEditSelectedChapter ? (
        <PrismBanner tone="caution">
          You can view this chapter&apos;s fair data, but only an admin or an officer from this chapter can edit it.
        </PrismBanner>
      ) : null}

      {chapters.length ? (
        <div className="project-fairs-chapter-tabs">
          {chapters.map((chapter) => (
            <PrismButton
              key={chapter.id}
              display={chapter.id === selectedChapterId ? "fill" : "ghost"}
              tone={chapter.id === selectedChapterId ? "primary-brand" : "secondary"}
              onPrismClick={() => setSelectedChapterId(chapter.id)}
            >
              {chapter.location}
            </PrismButton>
          ))}
        </div>
      ) : null}

      {/* The one piece of sequencing the pane enforces. Everything else is order-free. */}
      {selectedChapterId && !hasActiveEvent ? (
        <PrismBanner tone="caution">
          This chapter has no {selectedYear} event yet. Open <strong>Manage Events</strong> below and use{" "}
          <strong>+ New Event</strong> to create one — you&apos;ll give it a title and an anchor up front, and it
          stays a draft (invisible to the public) until you publish it.
        </PrismBanner>
      ) : null}

      <div className="project-fairs-manage-events">
        <PrismSelect
          label="Year"
          value={String(selectedYear)}
          options={[...new Set([...availableYears, selectedYear])]
            .sort((a, b) => b - a)
            .map((year) => ({ value: String(year), display: String(year) }))}
          onPrismChange={(e: CustomEvent<{ value?: string }>) =>
            setSelectedYear(parseInt(e.detail?.value || `${nowYear}`, 10))
          }
        />

        <div className="project-fairs-hotseat">
          <div className="project-fairs-hotseat-main">
            <span className="project-fairs-hotseat-anchor">
              {activeEvent ? `#${activeEvent.chapter_key}` : "No event selected"}
            </span>
            {activeEvent ? (
              <PrismButton
                tone="secondary"
                display="ghost"
                spacing="compact"
                setTitle="Copy this event's public link"
                onPrismClick={copyEventLink}
              >
                📋
              </PrismButton>
            ) : null}
          </div>
          <p className="project-fairs-hotseat-sub">
            {activeEvent
              ? `Owners: ${
                  ownerIdsOf(activeEvent)
                    .map((id) => chapters.find((chapter) => chapter.id === id)?.location)
                    .filter(Boolean)
                    .join(", ") || "none"
                } - ${activeEvent.draft ? "Draft" : "Published"}`
              : "Pick a chapter and event, or create one under Manage Events."}
          </p>
          <PrismButton
            className={`project-fairs-card-emoji-button${!selectedChapterId ? " project-fairs-card-emoji-button--off" : ""}`}
            tone="secondary"
            display="ghost"
            spacing="compact"
            setAriaLabel="Manage Events"
            setTitle={selectedChapterId ? "Manage Events" : "Select a chapter first"}
            disabled={!selectedChapterId}
            onPrismClick={() => {
              setEventActionError("");
              setOpenSection("identity");
            }}
          >
            <span className="project-fairs-card-emoji" aria-hidden="true">
              🎛️
            </span>
          </PrismButton>
        </div>

        <PrismSelect
          key={eventSelectResetKey}
          label="Events"
          value={selectedEventId ? String(selectedEventId) : ""}
          options={eventSelectOptions}
          onPrismChange={(e: CustomEvent<{ value?: string }>) => {
            const next = e.detail?.value || "";
            if (next === NEW_EVENT_OPTION) {
              // Deliberately leaves selectedEventId alone: the New Event dialog is a
              // detour, not a real selection change. The select is remounted (see
              // eventSelectResetKey) once the dialog closes so the transient option never
              // lingers in the DOM after Cancel.
              setShowNewEventModal(true);
              return;
            }
            setSelectedEventId(next ? parseInt(next, 10) : null);
          }}
        />
      </div>

      <section
        id={selectedChapterId ? `chapter-panel-${selectedChapterId}` : undefined}
        className="project-fairs-cards"
        aria-live="polite"
      >
        {sectionCards.map((card) => (
          <PrismCard key={card.key} display="static" headline={card.title} headlineTag="h3">
            <p className="project-fairs-card-summary">{card.summary}</p>
            <div className="project-fairs-card-action">
              <PrismButton
                className={`project-fairs-card-emoji-button${
                  !sectionsUnlocked ? " project-fairs-card-emoji-button--off" : ""
                }`}
                tone="secondary"
                display="ghost"
                spacing="compact"
                setAriaLabel={`Edit ${card.title}`}
                setTitle={sectionsUnlocked ? `Edit ${card.title}` : `Create an event first to edit ${card.title}`}
                disabled={!sectionsUnlocked}
                onPrismClick={() => setOpenSection(card.key)}
              >
                <span className="project-fairs-card-emoji" aria-hidden="true">
                  {card.emoji}
                </span>
              </PrismButton>
            </div>
          </PrismCard>
        ))}
      </section>

      <PrismButton
        className="project-fairs-preview-btn"
        tone="secondary"
        display="fill"
        disabled={!selectedChapterId}
        onPrismClick={() => setPreviewOpen(true)}
      >
        Preview
      </PrismButton>

      <div className="project-fairs-dialogs">
      <ManageEventsModal
        show={openSection === "identity"}
        activeEvent={activeEvent}
        chapters={chapters}
        saving={savingIdentity}
        publishing={publishing}
        deleting={deletingEvent}
        year={selectedYear}
        actionError={eventActionError}
        takenAnchors={anchorsForYear}
        onSave={saveIdentity}
        onTogglePublish={setEventDraftState}
        onDelete={deleteEvent}
        onOpenNewEvent={() => setShowNewEventModal(true)}
        onCancel={() => {
          setEventActionError("");
          setOpenSection(null);
        }}
      />

      <NewEventModal
        show={showNewEventModal}
        year={selectedYear}
        chapters={chapters}
        defaultChapterId={selectedChapterId}
        creating={creatingEvent}
        takenAnchors={anchorsForYear}
        error={createEventError}
        onCreate={createEvent}
        onCancel={closeNewEventModal}
      />

      <FairCopyModal
        show={openSection === "copy"}
        draft={draft}
        saving={saving}
        onChange={setDraft}
        onSave={saveSection}
        onCancel={cancelSection}
      />

      <SchedulePublishingModal
        show={openSection === "schedule"}
        draft={draft}
        saving={saving}
        onChange={setDraft}
        onSave={saveSection}
        onCancel={cancelSection}
      />

      <AttendeeToolsModal
        show={openSection === "access"}
        draft={draft}
        saving={saving}
        onChange={setDraft}
        onSave={saveSection}
        onCancel={cancelSection}
      />

      <ClosingBannerModal
        show={openSection === "closing"}
        draft={draft}
        saving={saving}
        onChange={setDraft}
        onSave={saveSection}
        onCancel={cancelSection}
      />

      <ApprovedProjectsModal
        show={openSection === "projects"}
        activeEvent={activeEvent}
        candidateProjects={candidateProjects}
        selectedProjectToAdd={selectedProjectToAdd}
        onSelectProjectToAdd={setSelectedProjectToAdd}
        onAddProject={addProject}
        onCreateProject={openCreateProject}
        onEditProject={openEditProject}
        onRemoveProject={removeProject}
        onClose={() => setOpenSection(null)}
      />

      <BulkImportModal
        show={openSection === "import"}
        fileInputKey={importFileKey}
        importHeaders={importHeaders}
        importMapping={importMapping}
        onImportMappingChange={(field, value) => setImportMapping((prev) => ({ ...prev, [field]: value }))}
        importPreviewRows={importPreviewRows}
        importFileName={importFileName}
        importing={importing || importingLogos}
        hasImportRows={!!importRows.length}
        onImportFile={handleImportFile}
        onTriggerConfirm={triggerImportConfirmation}
        showImportConfirm={showImportConfirm}
        onCancelConfirm={() => setShowImportConfirm(false)}
        onConfirmImport={importProjects}
        mappedRowCount={partitionedImportRows.mine.length}
        crossChapterSummary={crossChapterSummary}
        resubmissionGroups={dedupedImport.groups}
        keepAllFor={keepAllFor}
        onToggleKeepAll={(submitter, keepAll) =>
          setKeepAllFor((current) =>
            keepAll ? [...current, submitter] : current.filter((name) => name !== submitter)
          )
        }
        crossChapterChoice={crossChapterChoice}
        onCrossChapterChoiceChange={setCrossChapterChoice}
        canImportOtherChapters={isAdmin}
        peopleColumns={peopleColumns}
        unmatchedTags={importTagPreview.unmatched}
        selectedYear={selectedYear}
        includeLogos={includeLogos}
        onToggleIncludeLogos={(value) => {
          setIncludeLogos(value);
          if (!value) setLogoArchive(null);
        }}
        logoArchiveName={logoArchive?.name || ""}
        onLogoArchiveSelected={setLogoArchive}
        onClose={() => {
          setOpenSection(null);
          resetImportState();
        }}
      />

      <LogoMatchStepper
        show={showLogoStepper}
        unresolved={unresolvedLogos}
        autoAssignedCount={logoAssignedCount}
        projectOptions={(activeEvent?.ApprovedProjects || []).map((project) => ({
          id: Number(project.id),
          title: project.title || "Untitled",
        }))}
        onAssign={assignUnresolvedLogo}
        onClose={() => {
          setShowLogoStepper(false);
          setUnresolvedLogos([]);
        }}
      />

      <ProjectEditorModal
        open={editorOpen}
        mode={editorMode}
        eventId={activeEventId || undefined}
        project={editingProject || undefined}
        currentUser={currentUser}
        onClose={() => {
          setEditorOpen(false);
          setEditingProject(null);
        }}
        onSaved={handleProjectEditorSaved}
      />

      <PrismDialog
        open={previewOpen}
        close-method="auto"
        close-label="Close preview"
        className="project-fairs-preview-dialog"
        onPrismClose={() => setPreviewOpen(false)}
        setAriaLabel="Preview (unsaved draft)"
      >
        <span slot="header">Preview (unsaved draft)</span>
        <div className="project-fairs-preview-body">
          {previewOpen ? (
            <ProjectFair params={{ year: String(selectedYear) }} previewOverride={previewFairDetails} />
          ) : null}
        </div>
      </PrismDialog>
      </div>
    </AdminPane>
  );
};

export default ProjectFairs;
